import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Grid,
    CircularProgress,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    InputAdornment,
    TableSortLabel,
    Snackbar,
    Alert,
    TablePagination,
    Autocomplete
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentIcon from "@mui/icons-material/Assignment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { honorariosAdminSolicitudesJWT, honorariosAdminActualizarJWT, buscarAdministrativosJWT, honorariosAdminAsignarExpMasivoJWT, honorariosAdminCambiarEstadoMasivoJWT } from "../../api/endpoints";
import Checkbox from "@mui/material/Checkbox";
import SolicitudDetalleDialog from "./SolicitudDetalleDialog";

const getEstadoColor = (idEstado) => {
    switch (idEstado) {
        case 3: return "primary"; // Borrador
        case 4: return "default"; // Cerrada (Enviada)
        case 0: return "secondary"; // Pendiente / Observada?
        case 2: return "secondary"; // En Curso (Orange usually, but generic secondary here)
        case 1: return "primary"; // Pagada (Green usually)
        case -1: return "error"; // Anulada
        default: return "default";
    }
};

const getEstadoLabel = (idEstado) => {
    switch (idEstado) {
        case 3: return "Borrador";
        case 4: return "Enviada"; // Cerrada
        case 0: return "Pendiente";
        case 2: return "En Curso";
        case 1: return "Pagada";
        case -1: return "Anulada";
        default: return "Desconocido";
    }
};

export default function SolicitudesAdminTable() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    // Total real de coincidencias y tope del listado: sin esto la pantalla mostraba
    // 500 como si fueran todas, sin forma de saber que había más.
    const [totalGeneral, setTotalGeneral] = useState(null);
    const [limiteListado, setLimiteListado] = useState(500);
    const [vistaLote, setVistaLote] = useState('sin_asignar'); // 'sin_asignar', 'en_lotes', 'todas'
    const [filters, setFilters] = useState({
        fecha_desde: moment().subtract(1, "year").format("YYYY-MM-DD"),
        fecha_hasta: moment().format("YYYY-MM-DD"),
        estado: "", // All
        busqueda: "",
    });

    // Snackbar
    const [snackMsg, setSnackMsg] = useState({ open: false, text: '', severity: 'success' });

    // Selection for batch operations
    const [selected, setSelected] = useState([]);
    const [batchDialog, setBatchDialog] = useState({ open: false, type: '' }); // 'ASIGNAR_EXP', 'CAMBIAR_ESTADO'
    const [batchValue, setBatchValue] = useState('');
    const [batchLoading, setBatchLoading] = useState(false);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelected(sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(r => r.Id));
        } else {
            setSelected([]);
        }
    };

    const handleSelectOne = (e, id) => {
        e.stopPropagation();
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleBatchAction = async () => {
        if (!selected.length || !batchValue) return;
        setBatchLoading(true);
        try {
            let response;
            if (batchDialog.type === 'ASIGNAR_EXP') {
                response = await honorariosAdminAsignarExpMasivoJWT(selected, batchValue);
            } else {
                response = await honorariosAdminCambiarEstadoMasivoJWT(selected, batchValue);
            }
            if (response.ok) {
                setSnackMsg({ open: true, text: `${response.afectados} solicitudes actualizadas`, severity: 'success' });
                setBatchDialog({ open: false, type: '' });
                setBatchValue('');
                setSelected([]);
                fetchData();
            } else {
                setSnackMsg({ open: true, text: response.mensaje || 'Error', severity: 'error' });
            }
        } catch {
            setSnackMsg({ open: true, text: 'Error de conexión', severity: 'error' });
        }
        setBatchLoading(false);
    };

    // Exp admin search
    const [expOptions, setExpOptions] = useState([]);
    const [expSearching, setExpSearching] = useState(false);
    const expSearchTimer = React.useRef(null);

    const handleExpSearch = (q) => {
        if (expSearchTimer.current) clearTimeout(expSearchTimer.current);
        if (!q || q.length < 2) { setExpOptions([]); return; }
        expSearchTimer.current = setTimeout(async () => {
            setExpSearching(true);
            try {
                const r = await buscarAdministrativosJWT(q);
                setExpOptions(r?.expedientes || []);
            } catch { setExpOptions([]); }
            setExpSearching(false);
        }, 300);
    };

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Sort
    const [orderBy, setOrderBy] = useState('Id');
    const [order, setOrder] = useState('desc');

    const handleSort = (col) => {
        setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(col);
    };

    const parseDate = (d) => {
        if (!d || d === '-') return 0;
        const p = d.split('/');
        return p.length === 3 ? new Date(p[2], p[1] - 1, p[0]).getTime() : 0;
    };

    const filteredByLote = data.filter(row => {
        if (vistaLote === 'sin_asignar') return !row.Expediente_Adm;
        if (vistaLote === 'en_lotes') return !!row.Expediente_Adm;
        return true;
    });

    const sinAsignarCount = data.filter(r => !r.Expediente_Adm).length;
    const enLotesCount = data.filter(r => !!r.Expediente_Adm).length;

    const sortedData = [...filteredByLote].sort((a, b) => {
        let aVal = a[orderBy], bVal = b[orderBy];
        if (orderBy === 'Fecha' || orderBy === 'Fecha_Pago') { aVal = parseDate(aVal); bVal = parseDate(bVal); }
        if (typeof aVal === 'string') { aVal = aVal?.toLowerCase() || ''; bVal = bVal?.toLowerCase() || ''; }
        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
    });

    // Action Dialog State
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState(""); // ASIGNAR_EXP, CAMBIAR_ESTADO
    const [selectedSolicitud, setSelectedSolicitud] = useState(null);
    const [actionValue, setActionValue] = useState("");
    const [updating, setUpdating] = useState(false);

    // Detail Dialog State
    const [openDetailDialog, setOpenDetailDialog] = useState(false);
    const [detailSolicitudId, setDetailSolicitudId] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const fechaDesde = filters.fecha_desde ? filters.fecha_desde.replace(/-/g, "") : "";
            const fechaHasta = filters.fecha_hasta ? filters.fecha_hasta.replace(/-/g, "") : "";
            const idEstado = filters.estado || undefined;
            const busqueda = filters.busqueda || undefined;

            const response = await honorariosAdminSolicitudesJWT(fechaDesde, fechaHasta, idEstado, busqueda);
            if (response.ok) {
                setData(response.solicitudes || []);
                // `total` es el total real; el listado viene topeado en `limit` (500).
                setTotalGeneral(typeof response.total === 'number' ? response.total : null);
                setLimiteListado(response.limit || 500);
            }
        } catch (error) {
            console.error("Error fetching solicitudes admin", error);
        } finally {
            setLoading(false);
        }
    };

    // Cargar al abrir la pestaña. Sin esto la tabla arranca vacía y muestra
    // "No se encontraron solicitudes", que se lee como "no hay ninguna" en vez de
    // "todavía no busqué" — y las tarjetas de arriba sí traen datos, así que
    // parece que el listado perdió solicitudes.
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (prop) => (event) => {
        setFilters({ ...filters, [prop]: event.target.value });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchData();
    };

    const handleOpenDialog = (solicitud, type) => {
        setSelectedSolicitud(solicitud);
        setDialogType(type);
        if (type === "ASIGNAR_EXP") {
            setActionValue(solicitud.Expediente_Adm || "");
        } else if (type === "CAMBIAR_ESTADO") {
            setActionValue(solicitud.Id_Estado);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedSolicitud(null);
        setActionValue("");
    };

    const handleUpdate = async () => {
        setUpdating(true);
        try {
            const response = await honorariosAdminActualizarJWT(
                selectedSolicitud.Id,
                dialogType,
                actionValue
            );

            if (response.ok) {
                handleCloseDialog();
                fetchData();
                setSnackMsg({ open: true, text: 'Actualizado correctamente', severity: 'success' });
            } else {
                setSnackMsg({ open: true, text: response.mensaje || "Error al actualizar", severity: 'error' });
            }
        } catch (error) {
            console.error("Update error:", error);
            setSnackMsg({ open: true, text: "Error de conexión", severity: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    // View Detail logic
    const handleViewDetail = (id) => {
        setDetailSolicitudId(id);
        setOpenDetailDialog(true);
    };

    const handleCloseDetailDialog = () => {
        setOpenDetailDialog(false);
        setDetailSolicitudId(null);
    };

    return (
        <div>
            <Paper sx={{ mb: 2, p: 2 }}>
                <form onSubmit={handleSearch}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <TextField
                                label="Desde"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={filters.fecha_desde}
                                onChange={handleFilterChange("fecha_desde")}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <TextField
                                label="Hasta"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={filters.fecha_hasta}
                                onChange={handleFilterChange("fecha_hasta")}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <TextField
                                select
                                label="Estado"
                                fullWidth
                                value={filters.estado}
                                onChange={handleFilterChange("estado")}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="4">Enviada</MenuItem>
                                <MenuItem value="2">En Curso</MenuItem>
                                <MenuItem value="1">Pagada</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                label="Buscar (Nº, Matrícula, Apellido...)"
                                fullWidth
                                value={filters.busqueda}
                                onChange={handleFilterChange("busqueda")}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton type="submit" size="large">
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={fetchData}
                                disabled={loading}
                            >
                                {loading ? "Buscando..." : "Buscar"}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            {/* Chips de vista */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                <Chip
                    label={`Sin Asignar (${sinAsignarCount})`}
                    variant={vistaLote === 'sin_asignar' ? 'filled' : 'outlined'}
                    color={vistaLote === 'sin_asignar' ? 'warning' : 'default'}
                    onClick={() => { setVistaLote('sin_asignar'); setSelected([]); setPage(0); }}
                    sx={{ cursor: 'pointer' }}
                />
                <Chip
                    label={`En Lotes (${enLotesCount})`}
                    variant={vistaLote === 'en_lotes' ? 'filled' : 'outlined'}
                    color={vistaLote === 'en_lotes' ? 'success' : 'default'}
                    onClick={() => { setVistaLote('en_lotes'); setSelected([]); setPage(0); }}
                    sx={{ cursor: 'pointer' }}
                />
                <Chip
                    label={`Todas (${data.length.toLocaleString('es-AR')}${totalGeneral > data.length ? ` de ${totalGeneral.toLocaleString('es-AR')}` : ''})`}
                    variant={vistaLote === 'todas' ? 'filled' : 'outlined'}
                    onClick={() => { setVistaLote('todas'); setSelected([]); setPage(0); }}
                    sx={{ cursor: 'pointer' }}
                />
            </Box>

            {/* El listado viene topeado. Sin este aviso, las acciones masivas se aplican
                sobre un subconjunto que parece el total. */}
            {totalGeneral > data.length && (
                <Alert severity="info" sx={{ mb: 1, borderRadius: '4px' }}>
                    Hay <b>{totalGeneral.toLocaleString('es-AR')}</b> solicitudes en este filtro y se
                    muestran las <b>{limiteListado.toLocaleString('es-AR')}</b> más recientes.
                    Achicá el rango de fechas o filtrá por estado para verlas todas.
                </Alert>
            )}

            {/* Barra de acciones masivas */}
            {selected.length > 0 && (
                <Paper sx={{ p: 1.5, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#E8F5E9' }}>
                    <span style={{ fontWeight: 500 }}>{selected.length} solicitudes seleccionadas</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {vistaLote !== 'en_lotes' && (
                            <Button size="small" variant="outlined" startIcon={<AssignmentIcon />}
                                onClick={() => { setBatchDialog({ open: true, type: 'ASIGNAR_EXP' }); setBatchValue(''); setExpOptions([]); }}>
                                Asignar Exp. Adm.
                            </Button>
                        )}
                        <Button size="small" variant="outlined" startIcon={<EditIcon />}
                            onClick={() => { setBatchDialog({ open: true, type: 'CAMBIAR_ESTADO' }); setBatchValue(''); }}>
                            Cambiar Estado
                        </Button>
                    </div>
                </Paper>
            )}

            <TableContainer component={Paper}>
                {loading ? (
                    <div style={{ padding: 20, textAlign: "center" }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                                        checked={sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length > 0 && selected.length === sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                                        onChange={handleSelectAll}
                                        size="small"
                                    />
                                </TableCell>
                                {[
                                    { id: 'Id', label: 'ID' },
                                    { id: 'Fecha', label: 'Fecha' },
                                    { id: 'Profesional', label: 'Profesional' },
                                    { id: 'Expediente_Adm', label: 'Exp. Adm.' },
                                    { id: 'Id_Estado', label: 'Estado' },
                                    { id: 'Honorarios', label: 'Honorarios', align: 'right' },
                                ].map(col => (
                                    <TableCell key={col.id} align={col.align} sortDirection={orderBy === col.id ? order : false}>
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : 'asc'}
                                            onClick={() => handleSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                                <TableRow key={row.Id} hover onClick={() => { setDetailSolicitudId(row.Id); setOpenDetailDialog(true); }}
                                    sx={{ cursor: 'pointer', backgroundColor: selected.includes(row.Id) ? 'rgba(46,125,50,0.08)' : idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                        <Checkbox checked={selected.includes(row.Id)} onChange={(e) => handleSelectOne(e, row.Id)} size="small" />
                                    </TableCell>
                                    <TableCell>{row.Id}</TableCell>
                                    <TableCell>{row.Fecha}</TableCell>
                                    <TableCell>
                                        <div>{row.Profesional}</div>
                                        <small style={{ color: "#666" }}>Mat: {row.Matricula}</small>
                                    </TableCell>
                                    <TableCell>
                                        {row.Expediente_Adm ? (
                                            <span style={{ color: '#2e7d32', fontWeight: 500, cursor: 'default' }} title="Pertenece a un lote">
                                                {row.Expediente_Adm}
                                            </span>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getEstadoLabel(row.Id_Estado)}
                                            color={getEstadoColor(row.Id_Estado)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        $ {parseFloat(row.Honorarios).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                                    </TableCell>
                                    <TableCell align="center">
                                        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                                            <Tooltip title="Ver Detalle">
                                                <IconButton size="small" onClick={() => handleViewDetail(row.Id)}>
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Asignar Expediente Adm.">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    disabled={row.Id_Estado === -1 || !!row.Expediente_Adm}
                                                    onClick={(e) => { e.stopPropagation(); handleOpenDialog(row, "ASIGNAR_EXP"); }}
                                                >
                                                    <AssignmentIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Cambiar Estado">
                                                <IconButton
                                                    size="small"
                                                    color="secondary"
                                                    disabled={row.Id_Estado === -1}
                                                    onClick={(e) => { e.stopPropagation(); handleOpenDialog(row, "CAMBIAR_ESTADO"); }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {data.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No se encontraron solicitudes
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
                {sortedData.length > 0 && (
                    <TablePagination
                        component="div"
                        count={sortedData.length}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                )}
            </TableContainer>
            {/* Action Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle>
                    {dialogType === "ASIGNAR_EXP" ? "Asignar Expediente Adm." : "Cambiar Estado"}
                </DialogTitle>
                <DialogContent>
                    {dialogType === "ASIGNAR_EXP" && (
                        <Autocomplete
                            freeSolo
                            options={expOptions}
                            getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.Expediente_Adm} - ${opt.Extracto || opt.Caratula || ''}`}
                            filterOptions={(x) => x}
                            loading={expSearching}
                            onInputChange={(e, val) => { setActionValue(val); handleExpSearch(val); }}
                            onChange={(e, val) => {
                                if (val && typeof val !== 'string') {
                                    setActionValue(val.Expediente_Adm);
                                }
                            }}
                            renderOption={(props, opt) => (
                                <li {...props} key={opt.Id}>
                                    <div>
                                        <strong>{opt.Expediente_Adm}</strong>
                                        <div style={{ fontSize: 12, color: '#666' }}>{opt.Extracto || opt.Caratula}</div>
                                    </div>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    margin="dense"
                                    label="Buscar Expediente Administrativo"
                                    fullWidth
                                    helperText="Buscar por Nº Exp. o Extracto"
                                />
                            )}
                        />
                    )}
                    {dialogType === "CAMBIAR_ESTADO" && (
                        <TextField
                            select
                            label="Nuevo Estado"
                            fullWidth
                            margin="dense"
                            value={actionValue}
                            onChange={(e) => setActionValue(e.target.value)}
                        >
                            <MenuItem value="4">Enviada</MenuItem>
                            <MenuItem value="2">En Curso</MenuItem>
                            <MenuItem value="1">Pagada</MenuItem>
                        </TextField>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleUpdate} color="primary" variant="contained" disabled={updating}>
                        {updating ? "Guardando..." : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Detail Dialog */}
            <SolicitudDetalleDialog
                open={openDetailDialog}
                onClose={handleCloseDetailDialog}
                solicitudId={detailSolicitudId}
            />
            {/* Batch Dialog */}
            <Dialog open={batchDialog.open} onClose={() => setBatchDialog({ open: false, type: '' })} fullWidth maxWidth="sm">
                <DialogTitle>
                    {batchDialog.type === 'ASIGNAR_EXP' ? `Asignar Exp. Adm. a ${selected.length} solicitudes` : `Cambiar Estado de ${selected.length} solicitudes`}
                </DialogTitle>
                <DialogContent>
                    {batchDialog.type === 'ASIGNAR_EXP' && (
                        <Autocomplete
                            freeSolo
                            options={expOptions}
                            getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${opt.Expediente_Adm} - ${opt.Extracto || opt.Caratula || ''}`}
                            filterOptions={(x) => x}
                            loading={expSearching}
                            onInputChange={(e, val) => { setBatchValue(val); handleExpSearch(val); }}
                            onChange={(e, val) => { if (val && typeof val !== 'string') setBatchValue(val.Expediente_Adm); }}
                            renderOption={(props, opt) => (
                                <li {...props} key={opt.Id}>
                                    <div><strong>{opt.Expediente_Adm}</strong><div style={{ fontSize: 12, color: '#666' }}>{opt.Extracto || opt.Caratula}</div></div>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} autoFocus margin="dense" label="Buscar Expediente Administrativo" fullWidth helperText="Las solicitudes pasarán a estado En Curso" />
                            )}
                        />
                    )}
                    {batchDialog.type === 'CAMBIAR_ESTADO' && (
                        <TextField select label="Nuevo Estado" fullWidth margin="dense" value={batchValue} onChange={(e) => setBatchValue(e.target.value)}>
                            <MenuItem value="4">Enviada</MenuItem>
                            <MenuItem value="2">En Curso</MenuItem>
                            <MenuItem value="1">Pagada</MenuItem>
                        </TextField>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBatchDialog({ open: false, type: '' })}>Cancelar</Button>
                    <Button onClick={handleBatchAction} variant="contained" disabled={!batchValue || batchLoading}>
                        {batchLoading ? 'Procesando...' : 'Aplicar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackMsg.open} autoHideDuration={4000} onClose={() => setSnackMsg(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackMsg.severity} onClose={() => setSnackMsg(s => ({ ...s, open: false }))} variant="filled">
                    {snackMsg.text}
                </Alert>
            </Snackbar>
        </div>
    );
}
